/**
 * PD-2 — turning PD-1's payload into the rows the timeline draws.
 *
 * Kept as a pure module, apart from the components, for the same reason PD-1
 * kept `services/conversation-trace.ts` apart from its route: WHICH row belongs
 * WHERE on the screen is the part of this package that can be wrong in a way no
 * render test would catch. It is a function from one JSON payload to an ordered
 * array, and `trace.test.ts` is 40 assertions about that array.
 *
 * ## The three orderings, and why only one of them is authoritative
 *
 * `data.timeline` is already merged and sorted by PD-1 (`ts` primary,
 * `message_key` tiebreak, undated entries last). This module NEVER re-sorts it.
 * Turns come out in `seq` order, always — a screen that reorders the transcript
 * it was handed is showing a conversation that did not happen.
 *
 * The other two orderings are interleaved INTO that spine:
 *
 *   - match-event nodes (`/match_events/requests`, `/match_events/results`)
 *     carry their own `ts`;
 *   - reconcile passes that committed no turn carry `created_at`.
 *
 * Both are placed by timestamp relative to the turns around them, never by
 * re-sorting the whole list: a marker lands before the first turn strictly later
 * than it. Strictly, so a submit stamped at the same millisecond as the turn
 * that caused it is drawn AFTER that turn, which is the causal order. A marker
 * with no usable timestamp, or one later than every dated turn, goes at the end
 * and says `undated` so it is not read as "this happened last".
 *
 * ## Passes that committed nothing are rows, not silence
 *
 * `timeline[].verdicts` only attaches a pass to a turn when the pass COMMITTED
 * that turn (PD-1 §2.3). Three kinds of pass therefore attach to nothing:
 * superseded, failed, and a pass whose committed turn fell outside the
 * transcript window. All three are the reasoner deciding something, so all three
 * get their own row — dropping them would make a screen whose whole purpose is
 * "what did the reasoner decide" silently omit every decision that failed.
 *
 * ## Nothing here trusts the payload's types
 *
 * Verdict rows, directives, RTDB nodes and match-event nodes are passed through
 * by PD-1 exactly as stored, so every field read below goes through a tolerant
 * narrowing helper. A malformed row degrades to "unknown" in one cell; it never
 * throws and takes the screen with it.
 */
import type {
  ConversationTrace,
  TimelineEntry,
  TraceOrigin,
  TraceSourceName,
  TraceSourceStatus,
  VerdictArchiveRow,
} from '../../api/types';

// ---------------------------------------------------------------------------
// Tolerant readers
// ---------------------------------------------------------------------------

export function obj(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}
export function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
export function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
export function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** Epoch ms from an ISO string, or null. Never NaN — `new Date(NaN)` renders
 *  "Invalid Date" and a timestamp that reads as a word sorts nowhere. */
export function parseIso(v: unknown): number | null {
  const s = str(v);
  if (s === null) return null;
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : ms;
}

// ---------------------------------------------------------------------------
// A reconcile pass, narrowed
// ---------------------------------------------------------------------------

/** What a pass DID, in the one word an operator scanning the column needs.
 *  A failed pass is a failed pass even if it also carries `superseded`. */
export type PassOutcome = 'committed' | 'superseded' | 'failed' | 'no_commit';

export interface DirectiveView {
  mode: string | null;
  storyType: string | null;
  phaseNote: string | null;
  languageHint: string | null;
  objectives: { key: string; goal: string; oneAsk: boolean; privacySoft: boolean }[];
  doNotAsk: string[];
  picker: { field: string; reason: string | null } | null;
  ctas: { action: string; emitWhen: string; matchId: string | null }[];
  /** The turn's status event: what the executor told the client to show. */
  status: { kind: string; narrate: boolean; textHint: string | null } | null;
  notes: string[];
  refinementQuestions: string[];
  dotCount: number;
  computedAt: string | null;
  basedOnMessageId: string | null;
}

export interface DecisionView {
  storyType: string | null;
  reclassification: { from: string; to: string; reason: string } | null;
  submit: { action: string; reason: string | null } | null;
  requestSystemTurn: boolean;
  coverage: { covered: number; total: number; missing: string[] };
  askState: { unasked: number; asked: number; answered: number; declined: number };
  permanentDots: number;
  distress: boolean;
  safetyNote: string | null;
}

export interface VerdictView {
  /** Index into `ConversationTrace.verdicts`, so a reader can find the raw row. */
  index: number;
  raw: VerdictArchiveRow;
  createdAt: number | null;
  createdAtRaw: string | null;
  model: string | null;
  escalated: boolean;
  superseded: boolean;
  error: string | null;
  outcome: PassOutcome;
  latencyMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  messageKey: string | null;
  basedOnMessageKey: string | null;
  directiveVersion: string | null;
  directive: DirectiveView | null;
  decision: DecisionView | null;
}

export function viewDirective(raw: unknown): DirectiveView | null {
  const d = obj(raw);
  if (d === null) return null;
  const picker = obj(d.picker);
  const status = obj(d.status);
  return {
    mode: str(d.mode),
    storyType: str(d.story_type),
    phaseNote: str(d.phase_note),
    languageHint: str(d.language_hint),
    objectives: arr(d.objectives).flatMap((o) => {
      const item = obj(o);
      if (item === null) return [];
      return [
        {
          key: str(item.key) ?? '(unkeyed)',
          goal: str(item.goal) ?? '',
          oneAsk: item.one_ask === true,
          privacySoft: item.privacy_soft === true,
        },
      ];
    }),
    doNotAsk: arr(d.do_not_ask).flatMap((k) => (str(k) === null ? [] : [k as string])),
    picker: picker === null ? null : { field: str(picker.field) ?? '(unnamed)', reason: str(picker.reason) },
    ctas: arr(d.ctas).flatMap((c) => {
      const item = obj(c);
      if (item === null) return [];
      return [
        {
          action: str(item.action) ?? '(unnamed)',
          emitWhen: str(item.emit_when) ?? '',
          matchId: str(item.match_id),
        },
      ];
    }),
    status:
      status === null
        ? null
        : { kind: str(status.kind) ?? 'unknown', narrate: status.narrate === true, textHint: str(status.text_hint) },
    notes: arr(d.notes).flatMap((n) => (str(n) === null ? [] : [n as string])),
    refinementQuestions: arr(d.refinement_questions).flatMap((q) => (str(q) === null ? [] : [q as string])),
    dotCount: arr(d.dots).length,
    computedAt: str(d.computed_at),
    basedOnMessageId: str(d.based_on_message_id),
  };
}

export function viewDecision(raw: unknown): DecisionView | null {
  const v = obj(raw);
  if (v === null) return null;
  const coverage = obj(v.coverage) ?? {};
  const missing = Object.keys(coverage).filter((k) => coverage[k] === 'missing');
  const askState = obj(v.ask_state) ?? {};
  const tally = { unasked: 0, asked: 0, answered: 0, declined: 0 };
  for (const value of Object.values(askState)) {
    if (value === 'unasked' || value === 'asked' || value === 'answered' || value === 'declined') tally[value] += 1;
  }
  const submit = obj(v.submit);
  const reclass = obj(v.reclassification);
  const safety = obj(v.safety);
  return {
    storyType: str(v.story_type),
    reclassification:
      reclass === null
        ? null
        : {
            from: str(reclass.from) ?? '?',
            to: str(reclass.to) ?? '?',
            reason: str(reclass.reason) ?? '',
          },
    submit: submit === null ? null : { action: str(submit.action) ?? 'none', reason: str(submit.reason) },
    requestSystemTurn: v.request_system_turn === true,
    coverage: { covered: Object.keys(coverage).length - missing.length, total: Object.keys(coverage).length, missing },
    askState: tally,
    permanentDots: arr(v.permanent_dots).length,
    distress: safety?.distress === true,
    safetyNote: str(safety?.note),
  };
}

export function viewVerdict(raw: VerdictArchiveRow, index: number): VerdictView {
  const error = str(raw?.error);
  const superseded = raw?.superseded === true;
  const messageKey = str(raw?.message_key);
  return {
    index,
    raw: raw ?? {},
    createdAt: parseIso(raw?.created_at),
    createdAtRaw: str(raw?.created_at),
    model: str(raw?.model),
    escalated: raw?.escalated === true,
    superseded,
    error,
    // Precedence, not exclusivity: a pass can be marked superseded AND have
    // failed, and "failed" is the fact an operator is looking for.
    outcome: error !== null ? 'failed' : superseded ? 'superseded' : messageKey !== null ? 'committed' : 'no_commit',
    latencyMs: num(raw?.latency_ms),
    inputTokens: num(raw?.input_tokens),
    outputTokens: num(raw?.output_tokens),
    messageKey,
    basedOnMessageKey: str(raw?.based_on_message_key),
    directiveVersion: str(raw?.directive_version),
    directive: viewDirective(raw?.directive),
    decision: viewDecision(raw?.verdict),
  };
}

// ---------------------------------------------------------------------------
// Match-event markers
// ---------------------------------------------------------------------------

export interface EventView {
  /** RTDB node key — `{pebbleId}_{storyVersion}` for a request. */
  key: string;
  side: 'request' | 'result';
  ts: number | null;
  /** Request: the ingest status. Result: the contract `kind`. */
  label: string;
  storyVersion: number | null;
  source: string | null;
  clientEvent: string | null;
  mustTellUser: boolean | null;
  matchId: string | null;
  requestKey: string | null;
  attempts: number | null;
  error: string | null;
  node: Record<string, unknown>;
}

function viewEvent(raw: unknown, side: 'request' | 'result'): EventView | null {
  const n = obj(raw);
  if (n === null) return null;
  const payload = obj(n.payload);
  return {
    key: str(n.key) ?? '(no key)',
    side,
    ts: num(n.ts),
    label: (side === 'request' ? str(n.status) : str(n.kind)) ?? 'unknown',
    storyVersion: num(n.story_version),
    source: str(n.source),
    clientEvent: str(n.client_event),
    mustTellUser: typeof n.must_tell_user === 'boolean' ? n.must_tell_user : null,
    matchId: str(n.match_id) ?? str(payload?.match_id),
    requestKey: str(n.request_key),
    attempts: num(n.attempts),
    error: str(n.error),
    node: n,
  };
}

export function viewEvents(trace: Pick<ConversationTrace, 'match_events'>): EventView[] {
  const me = trace.match_events;
  if (me === null || me === undefined) return [];
  return [
    ...arr(me.requests).flatMap((r) => {
      const v = viewEvent(r, 'request');
      return v === null ? [] : [v];
    }),
    ...arr(me.results).flatMap((r) => {
      const v = viewEvent(r, 'result');
      return v === null ? [] : [v];
    }),
  ];
}

// ---------------------------------------------------------------------------
// The rows
// ---------------------------------------------------------------------------

export interface TurnRow {
  kind: 'turn';
  key: string;
  entry: TimelineEntry;
  /** The passes that committed this turn, OLDEST FIRST — the opposite of
   *  `data.verdicts`, which is `created_at DESC`. A copy is sorted; the
   *  payload's array is never touched, because the entries index into it. */
  verdicts: VerdictView[];
}
export interface PassRow {
  kind: 'pass';
  key: string;
  verdict: VerdictView;
  /** True when the pass DID commit a turn that is not in this window — the row
   *  is here so the decision is not silently dropped, and it says so. */
  offWindow: boolean;
  undated: boolean;
}
export interface EventRow {
  kind: 'event';
  key: string;
  event: EventView;
  undated: boolean;
}
export interface GapRow {
  kind: 'gap';
  key: string;
  beforeSeq: number;
  sources: TraceOrigin[];
}
export type TraceRow = TurnRow | PassRow | EventRow | GapRow;

type Marker = { at: number | null; row: PassRow | EventRow };

/**
 * The ordered rows of the timeline.
 *
 * Turns keep `data.timeline`'s order verbatim. Markers are spliced in by
 * timestamp; the gap seam is drawn at `timeline_meta.gap_before_seq` and
 * nowhere else.
 */
export function buildRows(trace: ConversationTrace): TraceRow[] {
  const entries = arr(trace?.timeline) as TimelineEntry[];
  const verdictRows = arr(trace?.verdicts) as VerdictArchiveRow[];
  const views = verdictRows.map(viewVerdict);

  // Which passes a turn claims. PD-1 computes this join server-side on
  // `message_key`; anything it did NOT attach becomes a row of its own below.
  const attached = new Set<number>();
  const perEntry = entries.map((e) => {
    const list: VerdictView[] = [];
    for (const i of arr(e?.verdicts)) {
      if (typeof i !== 'number') continue;
      const view = views[i];
      if (view === undefined) continue;
      attached.add(i);
      list.push(view);
    }
    // Oldest pass first: a turn re-reconciled after a supersede reads as a
    // sequence of decisions, and the newest-first payload order would tell it
    // backwards. `slice`-free because `list` is already a fresh array.
    list.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0) || a.index - b.index);
    return list;
  });

  const markers: Marker[] = [];
  views.forEach((view, i) => {
    if (attached.has(i)) return;
    markers.push({
      at: view.createdAt,
      row: {
        kind: 'pass',
        key: `pass-${i}`,
        verdict: view,
        // It committed a turn, and that turn is not on this screen — almost
        // always because the transcript window dropped it. Keyed on the
        // OUTCOME, not on `messageKey !== null`: a failed or superseded pass can
        // still carry the key it was working toward, and calling that
        // "committed a turn outside this window" would overwrite the one fact
        // the row exists to report.
        offWindow: view.outcome === 'committed',
        undated: view.createdAt === null,
      },
    });
  });
  // The array index is in the row key, not just the node key: a malformed node
  // has no key at all, two of them would collide, and React reuses the DOM of
  // the row it thinks it already drew — which on this screen means one match
  // event rendered with another's body.
  viewEvents(trace).forEach((event, i) => {
    markers.push({
      at: event.ts,
      row: { kind: 'event', key: `event-${i}-${event.side}-${event.key}`, event, undated: event.ts === null },
    });
  });
  // Dated markers ascending; undated ones last, in the order they arrived.
  markers.sort((a, b) => {
    if (a.at === null && b.at === null) return 0;
    if (a.at === null) return 1;
    if (b.at === null) return -1;
    return a.at - b.at;
  });

  const meta = trace?.timeline_meta;
  const gapSeq = meta?.head_truncated === true ? (meta.gap_before_seq ?? null) : null;
  const gapRow = (): GapRow => ({
    kind: 'gap',
    key: `gap-${gapSeq}`,
    beforeSeq: gapSeq ?? 0,
    sources: arr(meta?.truncated_sources) as TraceOrigin[],
  });

  const rows: TraceRow[] = [];
  let mi = 0;
  let gapDrawn = false;
  entries.forEach((entry, i) => {
    if (gapSeq !== null && !gapDrawn && entry?.seq === gapSeq) {
      rows.push(gapRow());
      gapDrawn = true;
    }
    const ts = num(entry?.ts);
    // Flush every marker strictly EARLIER than this turn. Equal timestamps stay
    // behind the turn: a submit stamped in the same millisecond as the commit
    // that caused it belongs after it. An undated turn flushes nothing — there
    // is nothing to compare against, and guessing would move real markers.
    if (ts !== null) {
      while (mi < markers.length && markers[mi].at !== null && (markers[mi].at as number) < ts) {
        rows.push(markers[mi].row);
        mi += 1;
      }
    }
    rows.push({
      kind: 'turn',
      // The array index leads, exactly as it does for pass and event rows. A
      // message key is unique per store, but the timeline is a MERGE of three
      // of them plus whatever a degraded payload hands over, so uniqueness is
      // not ours to assume — and a collision here folds one turn's reconcile
      // passes under another turn's provenance strip. The natural key stays in
      // the string so the key is still readable in a React trace.
      key: `turn-${i}-${entry?.message_key ?? entry?.pg_id ?? entry?.seq ?? i}`,
      entry,
      verdicts: perEntry[i],
    });
  });
  while (mi < markers.length) {
    rows.push(markers[mi].row);
    mi += 1;
  }
  // A seam whose seq names no entry still has to be drawn — at the end, which
  // is the only place that cannot claim turns are missing from somewhere they
  // are not. `gapBeforeSeq` returns 0 for a source with no visible row, so this
  // is the empty-timeline case and the out-of-range case.
  if (gapSeq !== null && !gapDrawn) rows.push(gapRow());
  return rows;
}

// ---------------------------------------------------------------------------
// Header summaries
// ---------------------------------------------------------------------------

export interface SourceIssue {
  name: TraceSourceName;
  status: TraceSourceStatus;
  /** `error` and `not_configured` are the two that set `degraded` upstream. */
  severity: 'error' | 'warning';
}

/** Sources worth showing at the top: broken, unconfigured, or truncated.
 *  `ok`, `skipped` and `not_found` are not problems and are not listed —
 *  `not_found` is a pebble that does not exist, which the page says elsewhere
 *  and in plain words. */
export function sourceIssues(trace: Pick<ConversationTrace, 'sources'>): SourceIssue[] {
  const sources = obj(trace?.sources) ?? {};
  const issues: SourceIssue[] = [];
  for (const [name, raw] of Object.entries(sources)) {
    const status = obj(raw) as TraceSourceStatus | null;
    if (status === null) continue;
    if (status.status === 'error' || status.status === 'not_configured') {
      issues.push({ name: name as TraceSourceName, status, severity: 'error' });
    } else if (status.truncated === true) {
      issues.push({ name: name as TraceSourceName, status, severity: 'warning' });
    }
  }
  return issues;
}

/** Roll-up counters for the header strip. Computed from the ROWS, so what it
 *  claims is on the screen is what is actually on the screen. */
export function summarizeRows(rows: TraceRow[]) {
  let turns = 0;
  let userTurns = 0;
  let aiTurns = 0;
  let systemTurns = 0;
  let duplicates = 0;
  let passes = 0;
  let failedPasses = 0;
  let supersededPasses = 0;
  let submits = 0;
  let events = 0;
  for (const row of rows) {
    if (row.kind === 'turn') {
      turns += 1;
      if (row.entry?.role === 'user') userTurns += 1;
      else if (row.entry?.role === 'ai') aiTurns += 1;
      else systemTurns += 1;
      if (row.entry?.possible_duplicate === true) duplicates += 1;
      for (const v of row.verdicts) {
        passes += 1;
        if (v.outcome === 'failed') failedPasses += 1;
        if (v.outcome === 'superseded') supersededPasses += 1;
        const action = v.decision?.submit?.action;
        if (action === 'submit' || action === 'resubmit') submits += 1;
      }
    } else if (row.kind === 'pass') {
      passes += 1;
      if (row.verdict.outcome === 'failed') failedPasses += 1;
      if (row.verdict.outcome === 'superseded') supersededPasses += 1;
      const action = row.verdict.decision?.submit?.action;
      if (action === 'submit' || action === 'resubmit') submits += 1;
    } else if (row.kind === 'event') {
      events += 1;
    }
  }
  return { turns, userTurns, aiTurns, systemTurns, duplicates, passes, failedPasses, supersededPasses, submits, events };
}

/** `1 turn` / `2 turns`. A counter strip that says "1 submit decisions" reads
 *  as a bug in the counter, which is the last thing a diagnostic screen should
 *  invite an operator to wonder about. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * `12:04:07.312` in LOCAL time — the resolution a reconcile trace is read at.
 *
 * A date-less clock is deliberate: the day is on the header, and every row
 * repeating it crowds out the part that differs. Hand-padded rather than
 * `toLocaleTimeString`, which emits a one-digit hour in several locales
 * (`4:00:00`) and turns a column of timestamps into a ragged edge — and which
 * has historically rendered midnight as `24:00:00` under `hour12: false`.
 */
export function formatClock(ms: number | null): string {
  if (ms === null) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}
